// How football turns Info Manager facts into directory rows.
//
// The one place that knows a quarterback fills a fantasy slot and a left tackle
// does not. The projection in @fsp/info supplies the machinery and never
// compares a position to a literal; this supplies the football.
//
// It maps the SOURCE's words, not our own, and that is the point of the OMOP
// pattern in the design: nflverse says `DEV` for a practice squad and
// `Did Not Participate In Practice` for a missed session, and those strings
// live here rather than being rewritten into our vocabulary at ingest time —
// so the fact table can still answer "what did nflverse actually say" and
// changing our mind costs an edit to this file rather than a re-ingest.

import type { DirectoryMapping, DirectoryStatus, PracticeLevel } from '@fsp/info';
import { nflTeams } from './teams';

/**
 * nflverse club codes that spell one of ours differently.
 *
 * Spelling variants only — older files' `WSH` and `JAC`. The Rams are `LA` in
 * every nflverse file since the move and in `teams.ts` too, which shows them
 * as `LAR`. A RELOCATED franchise's old code (`OAK`, `SD`, `STL`) is
 * deliberately absent: it names a different `team` entity in the schedules
 * back to 1999, and aliasing it would put a second Raiders row in the draft
 * room. Those clubs are skipped and counted instead.
 */
const CLUB_ALIASES: Record<string, string> = {
  WSH: 'WAS',
  JAC: 'JAX',
};

/**
 * nflverse position words → our `PositionId`s.
 *
 * Everything absent from this table maps to no fantasy position, which is a
 * real answer: offensive linemen, punters and long snappers fill no slot in
 * any league. They are still real people with real entities — they are simply
 * not draftable.
 *
 * DEFENDERS HAVE POSITIONS since 2026-10-18 (Q11, PLAN-2026-10-18-defensive-
 * players §3.2), grouped as the source's own vocabulary groups them
 * (`vocabulary.ts`). They are draftable only in a league whose roster starts
 * them: every pool reads the league's roster, so a nose tackle reaches the
 * draft room of a league of defensive players and no other.
 */
const POSITIONS: Record<string, readonly string[]> = {
  QB: ['QB'],
  RB: ['RB'],
  FB: ['RB'],
  HB: ['RB'],
  WR: ['WR'],
  TE: ['TE'],
  K: ['K'],
  PK: ['K'],
  // A team defense modeled as a roster ENTRY, which the simulator does and
  // nflverse never will — a D/ST is not a person and is not in players.csv. The
  // real feed's defenses come from `club()` below, as a row per `team` entity.
  DST: ['DST'],
  // Defensive players.
  DL: ['DL'],
  DE: ['DL'],
  DT: ['DL'],
  NT: ['DL'],
  EDGE: ['DL'],
  INTERIOR_LINE: ['DL'],
  LB: ['LB'],
  OLB: ['LB'],
  ILB: ['LB'],
  MLB: ['LB'],
  DB: ['DB'],
  CB: ['DB'],
  S: ['DB'],
  SS: ['DB'],
  FS: ['DB'],
  SAF: ['DB'],
  // The depth chart's own columns for the same players, by where they line up
  // (2026-10-22, B-479): a left end is a lineman, a right corner a back. The
  // roster file's coarse words above stay as they were, so nobody's positions
  // move; these give a defender his place on the chart.
  LDE: ['DL'],
  RDE: ['DL'],
  LDT: ['DL'],
  RDT: ['DL'],
  WLB: ['LB'],
  SLB: ['LB'],
  LILB: ['LB'],
  RILB: ['LB'],
  LOLB: ['LB'],
  ROLB: ['LB'],
  LCB: ['DB'],
  RCB: ['DB'],
  NB: ['DB'],
};

/** The order a player's positions are listed in: the sport's, offense first. */
const ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DST', 'DL', 'LB', 'DB'];

/** Whether a mapped answer is a defensive player's. */
const defensive = (positions: readonly string[]): boolean =>
  positions.some((p) => p === 'DL' || p === 'LB' || p === 'DB');

/**
 * nflverse roster status → whether they can appear in a game.
 *
 * Measured on roster_2026.csv: ACT 1,675 · DEV 557 · CUT 417 · RES 261 ·
 * INA 25 · RET 23 · EXE 5. `DEV` is the practice squad, which is its own status
 * rather than a kind of inactive: a practice-squad player can be elevated on
 * Saturday and score on Sunday, and a search box that hides them hides exactly
 * the players a waiver wire is for.
 */
const ROSTER_STATUS: Record<string, DirectoryStatus> = {
  ACT: 'active',
  DEV: 'practice-squad',
  RES: 'inactive',
  CUT: 'inactive',
  INA: 'inactive',
  EXE: 'inactive',
  RET: 'retired',
};

/**
 * A reserve list's finer code → the designation a card and a league's IR slot
 * read. Measured on 2026's week 6 rosters at the fantasy positions: R01 72,
 * R48 22, R04 6, R49 1. R01 is injured reserve and R48 the same, designated to
 * return; R04 is PUP (Zach Charbonnet's, after his knee) and R49 read as PUP,
 * designated to return, by the same pairing. Both kinds can fill an IR slot,
 * so a code read wrong costs a label, not a move. A code missing here leaves
 * the player without one, and so out of every league's pool, rather than
 * guessed into it.
 */
const RESERVE_DETAIL: Record<string, string> = {
  R01: 'IR',
  R48: 'IR',
  R04: 'PUP',
  R49: 'PUP',
};

/** The game-day designation → an `InjuryStatusDef` id from the definition. */
const REPORT_STATUS: Record<string, string> = {
  Out: 'O',
  Doubtful: 'D',
  Questionable: 'Q',
};

/**
 * The practice column, in words a person would read.
 *
 * nflverse publishes the league's own phrasing — "Did Not Participate In
 * Practice" — which is accurate and reads like a form. The source's wording
 * stays in the fact payload; this is what goes on a card.
 */
const PRACTICE_STATUS: Record<string, string> = {
  'Full Participation in Practice': 'Full practice',
  'Limited Participation in Practice': 'Limited practice',
  'Did Not Participate In Practice': 'Did not practice',
};

/** How much each of our practice wordings says he practiced (B-503). */
const PRACTICE_LEVEL: Record<string, PracticeLevel> = {
  'Full practice': 'full',
  'Limited practice': 'limited',
  'Did not practice': 'none',
};

/**
 * A generational suffix at the end of a family name: Jr., Sr., II to V, with
 * or without the period or a comma before it. Only at the end, and only after
 * a space or a comma, so "Vance" and "Ivory" are never cut.
 */
const GENERATIONAL_SUFFIX = /[\s,]+(?:jr|sr)\.?$|[\s,]+(?:ii|iii|iv|v)$/i;

export const footballDirectoryMapping: DirectoryMapping = {
  sport: 'football',

  positions(position, depthPosition) {
    // BOTH WORDS, MERGED (Q11). The roster's `position` is coarse (CB and FS
    // are both `DB`) and the depth chart's finer; for an offensive player the
    // two agree. With defenders mapped they can differ for a two-way player,
    // a receiver on the depth chart at cornerback, and taking only the first
    // that mapped would lose one of his positions; the big sites list him at
    // both. Offense first, in the sport's order.
    //
    // ONLY ACROSS THE LINE OF SCRIMMAGE (the review of Q11's backend): two
    // offensive words keep the old answer, the depth chart's, so a receiver
    // the depth chart lists at tight end stays a tight end and nothing an
    // existing league ranks by moves; likewise two defensive words.
    const of = (word: string | null) => (word === null ? undefined : POSITIONS[word.toUpperCase()]);
    const depth = of(depthPosition);
    const roster = of(position);
    const first = depth ?? roster ?? [];
    const other = depth !== undefined ? roster : undefined;
    if (other === undefined || defensive(first) === defensive(other)) return first;
    return ORDER.filter((p) => first.includes(p) || other.includes(p));
  },

  /**
   * THE WEEK'S ROSTER DECIDES, and the profile only ever says "retired".
   *
   * players.csv carries a `status` too, and it is that person's LAST recorded
   * status rather than their current one — a receiver who was ACT in 2011 and
   * has not played since still reads ACT today. Trusting it put 6,606 people in
   * the directory as active on the first real run, against 1,675 actually on an
   * NFL roster that week, which is a draft room full of people who retired
   * before the league had a practice-squad rule.
   *
   * So: on this week's roster, the roster's word; off it, `inactive` — they
   * cannot appear in a game, whatever they were once. Nobody is deleted
   * (ICD-P01 invariant 3); they keep their id, their record and their history.
   */
  status({ rosterStatus, profileStatus, onCurrentRoster }) {
    if (onCurrentRoster) {
      const hit = rosterStatus === null ? undefined : ROSTER_STATUS[rosterStatus];
      if (hit) return hit;
      // On a roster this week under a word nobody has mapped. They are
      // demonstrably employed, so `active` is the reading the evidence
      // supports — and the run already reported the word as unmapped, which is
      // the loud half. Defaulting to `inactive` instead would quietly drop a
      // live player out of every search the day the league renames a code.
      return 'active';
    }
    // Retirement is a claim somebody made, not an absence of one, so it is the
    // single thing the profile is still allowed to say here.
    if (profileStatus === 'RET') return 'retired';
    return 'inactive';
  },

  injuryStatus(reportStatus) {
    if (reportStatus === null) return null;
    return REPORT_STATUS[reportStatus] ?? null;
  },

  reserveStatus({ status, detail }) {
    if (status !== 'RES' || detail === null) return null;
    return RESERVE_DETAIL[detail] ?? null;
  },

  practiceStatus(practiceStatus) {
    if (practiceStatus === null) return null;
    // An unmapped phrasing passes through verbatim rather than becoming null.
    // The league's own wording on a card is worse than ours and far better
    // than nothing, and the unmapped report names it either way.
    return PRACTICE_STATUS[practiceStatus] ?? practiceStatus;
  },

  // OUR OWN WORDS, as the column holds them: an unmapped phrasing says
  // neither, and a bot reads the designation alone for it.
  practiceLevel(practice) {
    return PRACTICE_LEVEL[practice] ?? null;
  },

  /**
   * "B.Purdy": the initial of the name they go by, a dot, and the family name.
   *
   * nflverse's own style, measured on the 2,500 rows it supplies: this rule
   * reproduces every one of them except a dozen the league disambiguates by
   * hand ("Ti.Green", "Bry.Williams") or spells differently. The initial comes
   * from the DISPLAY name, not the legal first name, because that is what the
   * source does — Billy Brown, first name William, is "B.Brown".
   *
   * NO SUFFIX, and that is the source's style too: of the 57 people with a Jr.,
   * Sr. or numeral whose short name nflverse supplies, 56 drop it ("M.Penix",
   * "O.Beckham"). The source's own family name carries none — but a person the
   * roster knows and the player file does not has only a display name, split
   * on its first space, so his `last` is "Harrison Jr." (the N2 review, item 4).
   * The suffix is taken off here, so a derived name reads like the supplied one
   * beside it — the whole reason this exists is that a roster should not change
   * style from one row to the next.
   *
   * A one-word name is its own short name; ICD-P01 §6.1 caps the field at 24.
   */
  shortName({ display, last }) {
    const words = display.trim().split(/\s+/);
    const family = last.trim().replace(GENERATIONAL_SUFFIX, '');
    if (words.length < 2 || family === '') return display.trim().slice(0, 24) || null;
    const initial = [...words[0]!].find((c) => /\p{L}/u.test(c));
    if (initial === undefined) return family.slice(0, 24);
    return `${initial.toUpperCase()}.${family}`.slice(0, 24);
  },

  /**
   * HIS ESPN PAGE (B-482), whose status line ESPN keeps through game day,
   * from the ESPN id nflverse's players, rosters and depth chart files carry.
   * Digits only, so a key that is anything else makes no address.
   */
  playerPages(externalIds) {
    const espn = externalIds['espn'];
    return espn !== undefined && /^\d{1,12}$/.test(espn)
      ? [{ site: 'ESPN', url: `https://www.espn.com/nfl/player/_/id/${espn}` }]
      : [];
  },

  /**
   * THE CLUB IS THE D/ST. Every league's default roster has a `DST` slot, and
   * the thing that fills it is one of the 32 clubs — which the schedules
   * already assert as `team` entities. This names the club in words and says
   * which position its row fills; `@fsp/info` writes the row. The `code` kept
   * on the row is the SOURCE's spelling, because that is what
   * `players.pro_team` says for the people on that club. See DST.md.
   */
  club(keys) {
    for (const candidate of keys) {
      const code = candidate.trim().toUpperCase();
      if (code === '') continue;
      const team = nflTeams.find((t) => t.id === (CLUB_ALIASES[code] ?? code));
      if (team) {
        return {
          position: 'DST',
          code: candidate.trim(),
          name: team.name,
          shortName: team.shortName,
          location: team.location,
        };
      }
    }
    return null;
  },
};
